import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useHistory, useLocation } from "react-router-dom";
import { APPLICATION_GLOBAL_SLUG, handleApiCall, URLS } from "src/_config";
import FormTextInput from "src/_common/components/form-elements/textinput/formTextInput";
import { useToaster } from "src/_common/hooks/actions/common/appToasterHook";
import { useUserApi } from "src/_common/hooks/actions/user/appUserApiHook";
import PasswordField from "src/components/common/Form/PasswordField";
import SiteLogo from "src/components/common/SiteLogo";
import { useAuthStatus } from "src/_common/hooks/auth/authHook";

interface LoginWithEmailFormValues {
  email: string;
  password: string;
  rememberMe?: boolean;
}

const loginWithEmailSchema = yup.object().shape({
  email: yup.string().email("Please enter a valid email").required("Email is required"),
  password: yup.string().required("Password is required"),
});

function LoginWithEmail() {
  const userApi = useUserApi();
  const toast = useToaster();
  const history = useHistory();
  const location = useLocation();
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const [show2FA, setShow2FA] = useState<boolean>(false);
  const [otpValue, setOtpValue] = useState<string>("");
  const [tempLoginData, setTempLoginData] = useState<any>(null);
  const isAuthenticated = useAuthStatus();

  const { control, errors, setValue, handleSubmit } = useForm<LoginWithEmailFormValues>({
    resolver: yupResolver(loginWithEmailSchema),
    defaultValues: {
      email: "",
      password: "",
      rememberMe: false,
    },
  });

  // Check for verification success message
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const verified = urlParams.get("verified");

    if (verified === "true") {
      // toast.success("Email verified successfully! You can now log in to your account.");
      // Clean up the URL
      history.replace(URLS.AUTH.LOGIN);
    }
  }, [location, toast, history]);

  // Load saved credentials if rememberMe was used previously
  useEffect(() => {
    const savedEmail = localStorage.getItem("DR-JOBS-REMEMBER-EMAIL");
    const rememberMeFlag = localStorage.getItem("DR-JOBS-REMEMBER-ME");

    if (savedEmail && rememberMeFlag === "true") {
      setValue("email", savedEmail);
      setValue("rememberMe", true);
    }
  }, [setValue]);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const onSubmit = (data: any) => {
    // Check if postLoginRedirect exists BEFORE login
    const preLoginRedirect = localStorage.getItem("postLoginRedirect");
    console.log("🔎 BEFORE LOGIN - postLoginRedirect:", preLoginRedirect);

    data["game_slug"] = APPLICATION_GLOBAL_SLUG;

    // Store rememberMe preference for the login saga
    const loginData = {
      ...data,
      rememberMe: data.rememberMe || false,
    };

    // If remember me is unchecked, clear any existing saved credentials
    if (!loginData.rememberMe) {
      localStorage.removeItem("DR-JOBS-REMEMBER-EMAIL");
      localStorage.removeItem("DR-JOBS-REMEMBER-ME");
    }

    // Check again after clearing remember me items
    const afterClearRedirect = localStorage.getItem("postLoginRedirect");
    console.log("🔎 AFTER CLEARING REMEMBER ME - postLoginRedirect:", afterClearRedirect);

    // Custom API call wrapper to handle 401 errors
    userApi.callLoginWithEmail(
      loginData,
      (message: string, resp: any) => {
        if (!resp.requires_2fa) {
          toast.success(message);
          const postLoginRedirect = localStorage.getItem("postLoginRedirect");

          if (postLoginRedirect) {
            localStorage.removeItem("postLoginRedirect");
            history.push(postLoginRedirect);
          }
          // Call AUTH.DETAILS API after successful login
          handleApiCall(
            userApi.callGetMe,
            {},
            (userDetails) => {
              // Navigate to dashboard after getting user details
              history.push(URLS.DASHBOARD.MAIN);
            },
            toast,
            false,
            false
          );
        } else {
          // Store login data and show 2FA screen
          setTempLoginData(loginData);
          setShow2FA(true);
          toast.success("Please enter the 6-digit OTP sent to your device");
        }
      },
      (message: string, error?: any) => {
        // Check for 401 status specifically
        if (error?.status === 401 || error?.response?.status === 401) {
          toast.error("Invalid email or password. Please check your credentials and try again.");
        } else {
          toast.error(message || "Login failed. Please try again.");
        }
      }
    );
  };

  const handleOtpSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (otpValue.length !== 6) {
      toast.error("Please enter a valid 6-digit OTP");
      return;
    }

    const otpData = {
      ...tempLoginData,
      otp: otpValue,
    };

    // Call 2FA verification API
    userApi.callVerify2FA(
      otpData,
      (message: string, resp: any) => {
        toast.success(message);

        // Call AUTH.DETAILS API after successful 2FA verification
        handleApiCall(
          userApi.callGetMe,
          {},
          (userDetails) => {
            console.log("User details fetched:", userDetails);

            // Check for postLoginRedirect in localStorage
            const postLoginRedirect = localStorage.getItem("postLoginRedirect");
            console.log("🔍 [2FA] postLoginRedirect value:", postLoginRedirect);

            if (postLoginRedirect) {
              console.log("✅ [2FA] Redirecting to:", postLoginRedirect);
              // Remove the item and redirect to stored path
              localStorage.removeItem("postLoginRedirect");
              // Use window.location.href for full page redirect to bypass HOC
              window.location.href = postLoginRedirect;
            } else {
              console.log("❌ [2FA] No postLoginRedirect found, going to dashboard");
              // Navigate to dashboard after getting user details
              history.push(URLS.DASHBOARD.MAIN);
            }
          },
          toast,
          false,
          false
        );
      },
      (message: string, error?: any) => {
        toast.error(message || "Invalid OTP. Please try again.");
      }
    );
  };

  const handleBackToLogin = () => {
    setShow2FA(false);
    setOtpValue("");
    setTempLoginData(null);
  };

  const handleOtpChange = (value: string) => {
    // Only allow digits and limit to 6 characters
    const numericValue = value.replace(/[^0-9]/g, "").slice(0, 6);
    setOtpValue(numericValue);
  };

  const handleResendOTP = () => {
    if (!tempLoginData) {
      toast.error("Session expired. Please login again.");
      handleBackToLogin();
      return;
    }

    // Call the same login function to resend OTP
    userApi.callLoginWithEmail(
      tempLoginData,
      (message: string, resp: any) => {
        if (resp.requires_2fa) {
          toast.success("OTP has been resent to your device");
          setOtpValue(""); // Clear current OTP input
        } else {
          toast.error("Unable to resend OTP. Please try logging in again.");
          handleBackToLogin();
        }
      },
      (message: string, error?: any) => {
        toast.error(message || "Failed to resend OTP. Please try again.");
      }
    );
  };

  return (
    <div className="drjob-signup-container login-container">
      <div className="drjob-signup-left">
        <div className="drjob-login-content">
          <Link to={URLS.AUTH.LOGIN} className="logo-wrap">
            <SiteLogo className="medium-logo" style={{ width: "200px" }} />
          </Link>

          <div className="drjob-login-form">
            {!show2FA ? (
              <>
                <h2>Employer Login - Keep Connected</h2>
                <p className="drjob-login-subtitle">Enter your email and password to access your account</p>

                <form onSubmit={handleSubmit(onSubmit)}>
                  <div className="drjob-form-group">
                    <label>Email Address</label>
                    <div className="drjob-input-wrapper">
                      <Controller
                        control={control}
                        name="email"
                        render={({ onChange, onBlur, value, name, ref }) => (
                          <input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value}
                            ref={ref}
                            type="email"
                            placeholder="e.g. johnsmith@email.com"
                            className={errors.email ? "error" : ""}
                          />
                        )}
                      />
                      <i className="login-email-icon"></i>
                    </div>
                    {errors.email && <span className="error-text">{errors.email.message}</span>}
                  </div>

                  <div className="drjob-form-group">
                    <label>Password</label>
                    <div className="drjob-input-wrapper">
                      <Controller
                        control={control}
                        name="password"
                        render={({ onChange, onBlur, value, name, ref }) => (
                          <input
                            onChange={onChange}
                            onBlur={onBlur}
                            value={value}
                            ref={ref}
                            type={showPassword ? "text" : "password"}
                            placeholder="e.g. Enter your password"
                            className={errors.password ? "error" : ""}
                          />
                        )}
                      />
                      <button type="button" className="drjob-password-toggle" onClick={toggleShowPassword}>
                        {showPassword ? (
                          <i className="login-password-show-icon"></i>
                        ) : (
                          <i className="login-password-hide-icon"></i>
                        )}
                      </button>
                    </div>
                    {errors.password && <span className="error-text">{errors.password.message}</span>}
                  </div>

                  <div className="drjob-form-options">
                    <div className="drjob-checkbox">
                      <Controller
                        control={control}
                        name="rememberMe"
                        render={({ onChange, value }) => (
                          <label className="drjob-checkbox-label">
                            <input type="checkbox" checked={!!value} onChange={(e) => onChange(e.target.checked)} />
                            <span className="drjob-checkmark"></span>
                            Remember Me
                          </label>
                        )}
                      />
                    </div>
                    <Link to={URLS.AUTH.FORGOT_PASSWORD} className="drjob-forgot-link">
                      Forgot Password?
                    </Link>
                  </div>

                  <button type="submit" className="drjob-login-btn">
                    Login
                  </button>
                </form>

                <div className="drjob-signup-link">
                  Don't have an account? <Link to={URLS.AUTH.REGISTRATION}>Sign Up</Link>
                </div>
              </>
            ) : (
              <>
                <div className="drjob-2fa-header">
                  <button type="button" className="drjob-back-button" onClick={handleBackToLogin}>
                    ← Back to Login
                  </button>
                </div>

                <h2>Two-Factor Authentication</h2>
                <p className="drjob-login-subtitle">Enter the 6-digit code sent to your email.</p>

                <form onSubmit={handleOtpSubmit}>
                  <div className="drjob-form-group">
                    <label>6-Digit OTP</label>
                    <div className="drjob-otp-wrapper">
                      <input
                        type="text"
                        value={otpValue}
                        onChange={(e) => handleOtpChange(e.target.value)}
                        placeholder="000000"
                        className="drjob-otp-input"
                        maxLength={6}
                        autoComplete="one-time-code"
                      />
                      <i className="login-otp-icon"></i>
                    </div>
                    {otpValue.length > 0 && otpValue.length < 6 && (
                      <span className="error-text">Please enter all 6 digits</span>
                    )}
                  </div>

                  <button type="submit" className="drjob-login-btn" disabled={otpValue.length !== 6}>
                    Verify OTP
                  </button>
                </form>

                <div className="drjob-signup-link">
                  <p>
                    Didn't receive the code?{" "}
                    <button type="button" className="drjob-resend-link" onClick={handleResendOTP}>
                      Resend OTP
                    </button>
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
      <div className="drjob-signup-right">
        <img src="/images/login-banner.jpg" alt="Professional team collaboration" />
      </div>
    </div>
  );
}

export default LoginWithEmail;
